/**
 * useAuthentication React hook
 *
 * Handles authentication logic for the app, including token state, client detection,
 * redirect flows for universal and non-supported clients, and logout handling.
 *
 * Provides callbacks for when authentication is completed or needs to be triggered.
 *
 * @returns {{
 *   isTokenFetched: boolean,
 *   setTokenFetched: (value: boolean) => void,
 *   authorizedCallback: () => void,
 *   authenticateCallback: () => Promise<void>
 * }}
 *   isTokenFetched - Whether the authentication token has been fetched
 *   setTokenFetched - Setter for token fetched state
 *   authorizedCallback - Callback to run after successful authentication
 *   authenticateCallback - Callback to trigger authentication flow
 */

import {
  FIND_ACCOUNT,
  UNIVERSAL,
  clientUrls,
  liquidatedClients,
  nonSupportedClients
} from 'const/account/clients'
import config from 'const/config'
import routes, { Routes } from 'const/routes'
import authenticationRedirect, {
  AuthenticationRedirectProps
} from 'helpers/authenticationRedirect'
import handleLogout from 'helpers/handleLogout'
import isLwsEnabledForClients from 'helpers/isLwsEnabledForClients'
import isUniversalClient from 'helpers/isUniversalClient'
import redirectToLwsIDP from 'helpers/redirectToLwsIDP'
import redirectToUniversalIDP from 'helpers/redirectToUniversalIDP'
import redirectToUrl from 'helpers/redirectToUrl'
import { useState } from 'react'
import getQueryParam from 'syf-js-utilities/formats/getQueryParam'
import isMockData from 'syf-js-utilities/helpers/isMockData'
import { setSessionStorageItem } from 'utils/sessionStorageWithEvents'
import deepLinkStorage from 'helpers/deepLinkStorage'
import { setDeepLinkAnalyticsSession } from 'helpers/deepLinkAnalytics'
import { DEEP_LINK_FEATURES, DeepLinkFeature } from 'const/deepLink'
/**
 * Maps deeplink feature names to their corresponding app routes.
 * Used by all three deeplink combinations (feature param, feature+clientId, link param).
 */
const FEATURE_ROUTE_MAP = Object.freeze(
  Object.assign(Object.create(null), {
    [DEEP_LINK_FEATURES.creditview]: routes.CREDIT_VIEW,
    [DEEP_LINK_FEATURES.rewards]: routes.REWARDS,
    [DEEP_LINK_FEATURES.paymentsecurity]: routes.ACCOUNT_SERVICES
  })
) as Record<string, Routes>

const FEATURE_MODAL_MAP: Record<string, string> = Object.freeze(
  Object.assign(Object.create(null), {
    [DEEP_LINK_FEATURES.customcard]: 'CustomCardArt'
  })
)

const resolveDeepLinkRoute = (featureName: string): Routes | null =>
  FEATURE_ROUTE_MAP[featureName] ?? null

export function useAuthentication() {
  const client = getQueryParam('client') || localStorage.getItem('client')
  const prompt = getQueryParam('prompt')
  const legacyClient = getQueryParam('legacy_client')
  /*
   * Deep link parameters can come in three combinations:
   * 1. `feature` param is the primary way to specify a deep link destination (e.g. ?client=tjx&feature=rewards). It maps to a route via FEATURE_ROUTE_MAP.
   * 2. `clientId` param can be used alongside `feature` to specify an explicit account for multi-account users (e.g. ?client=universal&clientId=tjx&feature=rewards).
   * 3. `link` param is a legacy catch-all that also maps to FEATURE_ROUTE_MAP, but does not support clientId-based account selection (e.g. ?client=tjx&link=rewards).
   */
  const link = getQueryParam('link')
  const feature = getQueryParam('feature')
  const clientId = getQueryParam('clientId')
  const redirectUri = getQueryParam('redirection_uri')
  const token = getQueryParam('token')
  const clientConsumerKey = getQueryParam('client_consumer_key')
  const session = getQueryParam('session')
  const isUsingMockData = isMockData(config.APP_ENV)
  const [isTokenFetched, setTokenFetchedState] = useState(() => {
    const stored = sessionStorage.getItem('isTokenFetched')
    return stored === 'true'
  })

  // Setter that also persists to sessionStorage
  const setTokenFetched = (value: boolean) => {
    setTokenFetchedState(value)
    setSessionStorageItem('isTokenFetched', value ? 'true' : 'false')
  }

  /**
   * Callback to run after successful authentication.
   * Handles special logic for universal clients with prompt param.
   */
  const authorizedCallback = () => {
    if (isUniversalClient(client) && prompt) {
      // If universal client and prompt present, force logout and redirect to clear session
      const postLogoutRedirectUri = `${window.location.origin}${config.BASENAME}${routes.HOME}?client=universal&prompt=${prompt}&session=cleared`
      handleLogout(postLogoutRedirectUri)
      return
    }
    setTokenFetched(true)
  }

  const handleLwsRedirect = () => {
    sessionStorage.setItem('lwsRedirectUri', redirectUri)
    if (isLwsEnabledForClients(redirectUri)) {
      redirectToLwsIDP(token, clientConsumerKey)
    } else {
      const lwsErrorUrl = `${window.location.origin}${config.BASENAME}${routes.LWS_ERROR}`
      window.location.href = lwsErrorUrl
    }
  }

  const migrateMeV2Flow = (): boolean => {
    if (isUniversalClient(client) && prompt) {
      redirectToUniversalIDP({ client: UNIVERSAL, prompt, legacyClient })
      return true
    }
    return false
  }

  const buildDeepLinkOptions = (): AuthenticationRedirectProps | null => {
    const deepLinkFeature = (feature || link) as DeepLinkFeature

    if (!deepLinkFeature) return null

    // Modal-based deep links: store feature for post-auth handling in Dashboard
    if (FEATURE_MODAL_MAP[deepLinkFeature]) {
      deepLinkStorage.setClient(client, clientId)
      deepLinkStorage.setFeature(deepLinkFeature)
      setDeepLinkAnalyticsSession({
        client: client || '',
        clientId: clientId || '',
        feature: deepLinkFeature
      })
      return null
    }

    const deepLinkRoute = resolveDeepLinkRoute(deepLinkFeature)
    if (!deepLinkRoute) return null

    deepLinkStorage.setClient(client, clientId)
    deepLinkStorage.setFeature(deepLinkFeature)

    setDeepLinkAnalyticsSession({
      client: client || '',
      clientId: clientId || '',
      feature: deepLinkFeature
    })
    return {
      navigateTo: JSON.stringify({ route: deepLinkRoute }),
      scope: 'read write'
    }
  }

  const redirectByClientType = (isLiquidatedOrNonSupportedClient: boolean) => {
    // If client is supported and not liquidated, redirect for authentication
    if (
      Object.keys(clientUrls).includes(client) &&
      !isLiquidatedOrNonSupportedClient
    ) {
      authenticationRedirect(buildDeepLinkOptions())
    } else if (isUniversalClient(client)) {
      const deepLinkOptions = buildDeepLinkOptions()
      redirectToUniversalIDP({ client, prompt, ...(deepLinkOptions ?? {}) })
    } else {
      // Fallback: redirect to find account page
      redirectToUrl(FIND_ACCOUNT)
    }
  }

  /**
   * Callback to trigger authentication flow.
   * Handles mock data, client storage, and redirect logic for various client types.
   */
  const authenticateCallback = async () => {
    if (isUsingMockData) return
    if (client) localStorage.setItem('client', client)

    // Check if client is liquidated or not supported
    const isLiquidatedOrNonSupportedClient =
      liquidatedClients.includes(client) || nonSupportedClients.includes(client)

    if (legacyClient && migrateMeV2Flow()) return

    if (prompt && isUniversalClient(client) && session !== 'cleared') {
      // Special handling for universal client with prompt and uncleared session
      const postLogoutRedirectUri = `${window.location.origin}${config.BASENAME}${routes.HOME}?client=universal&prompt=${prompt}&session=cleared`
      await handleLogout(postLogoutRedirectUri)
      return
    }

    // LWS enabled clients redirection
    if (redirectUri && token) {
      handleLwsRedirect()
      return
    }

    redirectByClientType(isLiquidatedOrNonSupportedClient)
  }

  return {
    isTokenFetched,
    setTokenFetched,
    authorizedCallback,
    authenticateCallback
  }
}
